import React from 'react';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaTelegram } from 'react-icons/fa';
import { Phone, Clock, MapPin, Mail } from 'lucide-react';

const WHATSAPP_NUMBER = "2349152474245";
const PHONE_NUMBER = "09152474245";

const contactMethods = [
  {
    icon: FaWhatsapp,
    title: 'WhatsApp',
    description: 'Chat with us instantly',
    value: PHONE_NUMBER,
    action: () => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=Hello%20OX%20Olorire!`, '_blank'),
    buttonText: 'Open WhatsApp',
    buttonClass: 'btn-whatsapp',
    iconColor: '#25D366',
  },
  {
    icon: Phone,
    title: 'Phone',
    description: 'Call us directly',
    value: PHONE_NUMBER,
    action: () => window.open(`tel:${PHONE_NUMBER}`),
    buttonText: 'Call Now',
    buttonClass: 'bg-secondary-main hover:bg-secondary-hover text-white font-bold rounded-full px-6 py-3 transition-all',
    iconColor: '#3B82F6',
  },
];

const businessHours = [
  { day: 'Monday - Friday', hours: '8:00 AM - 10:00 PM' },
  { day: 'Saturday', hours: '9:00 AM - 8:00 PM' },
  { day: 'Sunday', hours: '10:00 AM - 6:00 PM' },
];

export default function ContactPage() {
  return (
    <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            <span className="text-text-primary">Get In </span>
            <span className="gold-gradient-text">Touch</span>
          </h1>
          <p className="text-text-secondary text-lg max-w-xl mx-auto">
            Have questions? We're here to help. Reach out through any of our contact channels.
          </p>
        </motion.div>

        {/* Contact Methods */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {contactMethods.map((method, index) => (
            <motion.div
              key={method.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass-card-gold p-8 text-center"
              data-testid={`contact-${method.title.toLowerCase()}`}
            >
              <div 
                className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${method.iconColor}20` }}
              >
                <method.icon size={32} style={{ color: method.iconColor }} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">
                {method.title}
              </h3>
              <p className="text-text-secondary mb-2">{method.description}</p>
              <p className="text-text-primary font-mono text-lg mb-6">{method.value}</p>
              <motion.button
                onClick={method.action}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={method.buttonClass}
                data-testid={`${method.title.toLowerCase()}-btn`}
              >
                {method.buttonText}
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* Business Hours */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-8"
          data-testid="business-hours"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary-main/10 flex items-center justify-center">
              <Clock size={24} className="text-primary-main" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary">
              Business Hours
            </h3>
          </div>
          
          <div className="space-y-4">
            {businessHours.map((schedule) => (
              <div 
                key={schedule.day}
                className="flex justify-between items-center py-3 border-b border-border last:border-0"
              >
                <span className="text-text-secondary">{schedule.day}</span>
                <span className="text-text-primary font-medium">{schedule.hours}</span>
              </div>
            ))}
          </div>

          <p className="text-text-muted text-sm mt-6">
            * We may respond outside business hours via WhatsApp for urgent requests.
          </p>
        </motion.div>

        {/* Quick Contact CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="glass-card-gold p-8 inline-block">
            <h3 className="font-heading text-2xl font-semibold text-text-primary mb-4">
              Ready to Trade?
            </h3>
            <p className="text-text-secondary mb-6">
              Start your crypto journey today with OX Olorire Exchange
            </p>
            <motion.a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hello%20OX%20Olorire!%20I%20want%20to%20start%20trading.`}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-whatsapp inline-flex items-center gap-2"
              data-testid="contact-cta-whatsapp"
            >
              <FaWhatsapp size={20} />
              Start Trading on WhatsApp
            </motion.a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
